import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { AlertCircle, Check, Pencil, RefreshCw, X } from 'lucide-react';

import { PageHeader } from '@/components/layout/PageHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useScoutPrograms, useUpdateScoutProgram } from '@/hooks/useScout';
import type { ScoutProgram } from '@repo/types';

export const Route = createFileRoute('/admin/scout/programs')({
  component: ScoutProgramsPage,
});

function ScoutProgramsPage() {
  const { data, isLoading, isFetching, error, refetch } = useScoutPrograms();
  const programs = useMemo(() => data ?? [], [data]);
  const [editing, setEditing] = useState<string | null>(null);

  return (
    <div className="space-y-6 p-4 duration-500 ease-out animate-in fade-in slide-in-from-bottom-6 sm:p-6">
      <PageHeader
        title="Scout Programs"
        description="Each program groups the employers worth watching for it, and records the roles those employers hire for."
        actions={
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={isFetching ? 'size-4 animate-spin' : 'size-4'} />
            Refresh
          </Button>
        }
      />

      <Alert>
        <AlertCircle className="size-4" />
        <AlertDescription>
          These keywords drive the extension&apos;s warning when a role filter does not fit the
          selected programs — &ldquo;ai intern&rdquo; over MBA-BBA - Finance, say. It only ever
          warns; the operator can always scrape anyway. Generic job words
          (<span className="font-mono text-xs">engineer</span>,{' '}
          <span className="font-mono text-xs">analyst</span>,{' '}
          <span className="font-mono text-xs">intern</span>) are deliberately absent: they appear in
          every discipline, so adding one would silence the warning for that program entirely.
        </AlertDescription>
      </Alert>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertDescription>{error.message || 'Failed to load scout programs.'}</AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {programs.map((program) => (
            <ProgramCard
              key={program.id}
              program={program}
              editing={editing === program.id}
              onEdit={() => setEditing(program.id)}
              onDone={() => setEditing(null)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ProgramCard({
  program,
  editing,
  onEdit,
  onDone,
}: {
  program: ScoutProgram;
  editing: boolean;
  onEdit: () => void;
  onDone: () => void;
}) {
  // Comma-separated is the right editor here: these are dozens of short tokens,
  // and a chip UI would make bulk paste-and-tidy — the actual workflow — harder.
  const [draft, setDraft] = useState(program.roleKeywords.join(', '));
  const update = useUpdateScoutProgram();

  // Re-seed on every entry into edit mode, not just on mount: clicking Edit on
  // another card leaves this one silently (parent moves `editing`), keeping an
  // abandoned draft — and the server lowercases/dedupes/sorts on save, so even
  // a saved draft diverges from what is now stored.
  const startEditing = () => {
    setDraft(program.roleKeywords.join(', '));
    onEdit();
  };

  const save = () => {
    const roleKeywords = draft
      .split(',')
      .map((keyword) => keyword.trim())
      .filter(Boolean);
    update.mutate({ id: program.id, roleKeywords }, { onSuccess: onDone });
  };

  const cancel = () => {
    setDraft(program.roleKeywords.join(', '));
    onDone();
  };

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="space-y-0.5">
            <p className="font-medium">{program.name}</p>
            <p className="text-xs text-muted-foreground">
              {program.targetCount} career site{program.targetCount === 1 ? '' : 's'} ·{' '}
              {program.roleKeywords.length} keyword{program.roleKeywords.length === 1 ? '' : 's'}
            </p>
          </div>
          {editing ? (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={cancel} disabled={update.isPending}>
                <X className="size-4" />
                Cancel
              </Button>
              <Button size="sm" onClick={save} disabled={update.isPending}>
                <Check className="size-4" />
                Save
              </Button>
            </div>
          ) : (
            <Button size="sm" variant="outline" onClick={startEditing}>
              <Pencil className="size-4" />
              Edit
            </Button>
          )}
        </div>

        {editing ? (
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="comma, separated, keywords"
            className="font-mono text-xs"
            autoFocus
          />
        ) : program.roleKeywords.length ? (
          <div className="flex flex-wrap gap-1">
            {program.roleKeywords.map((keyword) => (
              <Badge key={keyword} variant="outline" className="font-mono text-[10px] font-normal">
                {keyword}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            No vocabulary recorded — this program will never trigger a mismatch warning.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
